import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';
import { addDays } from 'date-fns/addDays';
import { addHours } from 'date-fns/addHours';
import { addMinutes } from 'date-fns/addMinutes';
import { addMonths } from 'date-fns/addMonths';
import { addSeconds } from 'date-fns/addSeconds';
import { addWeeks } from 'date-fns/addWeeks';
import { addYears } from 'date-fns/addYears';
import { differenceInDays } from 'date-fns/differenceInDays';
import { differenceInHours } from 'date-fns/differenceInHours';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { differenceInSeconds } from 'date-fns/differenceInSeconds';
import { differenceInWeeks } from 'date-fns/differenceInWeeks';
import { differenceInYears } from 'date-fns/differenceInYears';
import { endOfDay } from 'date-fns/endOfDay';
import { endOfHour } from 'date-fns/endOfHour';
import { endOfMonth } from 'date-fns/endOfMonth';
import { endOfQuarter } from 'date-fns/endOfQuarter';
import { endOfWeek } from 'date-fns/endOfWeek';
import { endOfYear } from 'date-fns/endOfYear';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfHour } from 'date-fns/startOfHour';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfQuarter } from 'date-fns/startOfQuarter';
import { startOfWeek } from 'date-fns/startOfWeek';
import { startOfYear } from 'date-fns/startOfYear';
import { subDays } from 'date-fns/subDays';
import { subHours } from 'date-fns/subHours';
import { subMinutes } from 'date-fns/subMinutes';
import { subMonths } from 'date-fns/subMonths';
import { subSeconds } from 'date-fns/subSeconds';
import { subWeeks } from 'date-fns/subWeeks';
import { subYears } from 'date-fns/subYears';

import { serializeDatetime } from '@/lib/dateFormatter';

import { applyTemporalBoundary, applyTemporalUnit, diffTemporalUnit } from './temporal';
import { workspaceTimezone } from './timezone';
import type {
  TaskTimeAddUnit,
  TaskTimeBoundaryUnit,
  TaskUtils,
  TaskWorkspaceFields
} from './types';
import { caughtErrorDetail, warnSdkUtilFallback } from './warn';

const addTimeUnits = {
  seconds: addSeconds,
  minutes: addMinutes,
  hours: addHours,
  days: addDays,
  weeks: addWeeks,
  months: addMonths,
  years: addYears
} satisfies Record<TaskTimeAddUnit, (date: Date, amount: number) => Date>;

const subtractTimeUnits = {
  seconds: subSeconds,
  minutes: subMinutes,
  hours: subHours,
  days: subDays,
  weeks: subWeeks,
  months: subMonths,
  years: subYears
} satisfies Record<TaskTimeAddUnit, (date: Date, amount: number) => Date>;

const diffTimeUnits = {
  seconds: differenceInSeconds,
  minutes: differenceInMinutes,
  hours: differenceInHours,
  days: differenceInDays,
  weeks: differenceInWeeks,
  months: differenceInMonths,
  years: differenceInYears
} satisfies Record<TaskTimeAddUnit, (end: Date, start: Date) => number>;

const startOfTimeUnits = {
  hour: startOfHour,
  day: startOfDay,
  week: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  month: startOfMonth,
  quarter: startOfQuarter,
  year: startOfYear
} satisfies Record<TaskTimeBoundaryUnit, (date: Date) => Date>;

const endOfTimeUnits = {
  hour: endOfHour,
  day: endOfDay,
  week: (date: Date) => endOfWeek(date, { weekStartsOn: 1 }),
  month: endOfMonth,
  quarter: endOfQuarter,
  year: endOfYear
} satisfies Record<TaskTimeBoundaryUnit, (date: Date) => Date>;

export function addTime(
  value: string | null | undefined,
  amount: number,
  unit: TaskTimeAddUnit
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = applyTemporalUnit(parseISO(value), amount, unit, addTimeUnits);
  return serializeDatetime(result) as string;
}

export function subtractTime(
  value: string | null | undefined,
  amount: number,
  unit: TaskTimeAddUnit
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = applyTemporalUnit(parseISO(value), amount, unit, subtractTimeUnits);
  return serializeDatetime(result) as string;
}

export function diffTime(
  a: string | null | undefined,
  b: string | null | undefined,
  unit: TaskTimeAddUnit = 'minutes'
): number | null {
  if (a === null || a === undefined || a === '' || b === null || b === undefined || b === '') {
    return null;
  }

  const start = parseISO(a);
  const end = parseISO(b);

  return diffTemporalUnit(start, end, unit, diffTimeUnits);
}

export function startOfTime(
  value: string | null | undefined,
  unit: TaskTimeBoundaryUnit
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = applyTemporalBoundary(parseISO(value), unit, startOfTimeUnits);
  return serializeDatetime(result) as string;
}

export function endOfTime(
  value: string | null | undefined,
  unit: TaskTimeBoundaryUnit
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const result = applyTemporalBoundary(parseISO(value), unit, endOfTimeUnits);
  return serializeDatetime(result) as string;
}

export function formatTime(
  value: string | null | undefined,
  format: string = "yyyy-MM-dd'T'HH:mm:ssXXX",
  timezone?: string
): string | null {
  if (!value) {
    warnSdkUtilFallback('time.format', 'No datetime was provided; returning null.');
    return null;
  }

  try {
    const trimmedTz = timezone?.trim();
    const tz = trimmedTz !== undefined && trimmedTz !== '' ? trimmedTz : 'UTC';
    return formatInTimeZone(parseISO(value), tz, format);
  } catch (error) {
    warnSdkUtilFallback(
      'time.format',
      `The datetime could not be formatted: ${caughtErrorDetail(error)}. Returning null.`
    );
    return null;
  }
}

export function currentTime(): string {
  return serializeDatetime(new Date()) as string;
}

export function createTimeUtils(workspaceFields: TaskWorkspaceFields): TaskUtils['time'] {
  return {
    add: addTime,
    subtract: subtractTime,
    diff: diffTime,
    startOf: startOfTime,
    endOf: endOfTime,
    format: (value, format, timezone) =>
      formatTime(value, format, timezone?.trim() || workspaceTimezone(workspaceFields)),
    current: currentTime
  };
}
